import { AiOutlineCloseCircle } from "react-icons/ai"
import Modal from "../../common/Modal"
import { useAdmin } from "../../../context/admin"
import { FormEvent, useState } from "react"
import { Blog } from "../../../types/common.types"
import FileInput from "../../common/FileInput"
import GeneralInput from "../../common/GeneralInput"
import AdminService from "../../../services/admin.service"
import jsonToFormData from "../../../utils/jsonToFormData"
import { useGlobal } from "../../../context/global"

// Function to convert image to WebP
async function convertToWebP(file: File, quality = 0.85): Promise<File> {
  return new Promise((resolve, reject) => {
    // Check if file is already WebP
    if (file.type === 'image/webp') {
      resolve(file);
      return;
    }

    // Check if file is a string (already uploaded file URL/path)
    if (typeof file === 'string') {
      // If it's already a string (file path/URL), return as is
      resolve(file as unknown as File);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0);
        
        // Convert to WebP with quality settings
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not convert image to WebP'));
              return;
            }
            
            // Create new File object with WebP extension
            const webpFile = new File(
              [blob],
              `${file.name.split('.')[0] || 'blog-cover'}.webp`,
              { type: 'image/webp' }
            );
            resolve(webpFile);
          },
          'image/webp',
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
}

export default function EditBlog() {
    const { editBlog, setEditBlog } = useAdmin()
    const { setIsLoading, isLoading, setMessage } = useGlobal()
    const [data, setData] = useState<Blog>(editBlog || {} as Blog)
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    function onChange(e: React.ChangeEvent<HTMLInputElement>) {
        const name = e.target.name
        const value = e.target.value

        setData(prevData => ({...prevData, [name]: value}))
    }

    // Function to handle file upload with WebP conversion
    const handleFileChange = async (newFile: File) => {
      // Validate file size (e.g., max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (newFile.size > maxSize) {
        setMessage({
          type: 'error',
          content: `File too large (${Math.round(newFile.size / 1024 / 1024)}MB). Max size: 5MB`
        });
        setTimeout(() => setMessage(undefined), 3000);
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/heic', 'image/webp'];
      if (!validTypes.includes(newFile.type)) {
        setMessage({
          type: 'error',
          content: 'Invalid file type. Please upload an image (JPEG, PNG, HEIC, or WebP)'
        });
        setTimeout(() => setMessage(undefined), 3000);
        return;
      }

      try {
        setIsLoading(true);
        setMessage({
          type: 'success',
          content: 'Converting cover image to WebP...'
        });
        
        const webpFile = await convertToWebP(newFile);
        
        // Calculate size reduction
        const sizeReduction = Math.round((1 - webpFile.size / newFile.size) * 100);
        
        setData(prevData => ({...prevData, cover: webpFile}));
        setMessage({
          type: 'success',
          content: `Cover converted to WebP (${Math.round(webpFile.size / 1024)}KB, ${sizeReduction}% smaller)`
        });
        
        setTimeout(() => {
          setMessage(undefined);
        }, 3000);
      } catch (error) {
        console.error('Error converting to WebP:', error);
        setMessage({
          type: 'error',
          content: 'Failed to convert image. Uploading original format.'
        });
        // Fallback: use original file
        setData(prevData => ({...prevData, cover: newFile}));
        setTimeout(() => {
          setMessage(undefined);
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };

    async function deleteBlog() {
      if (!editBlog?.id) return
      
      // Confirmation dialog
      const confirmed = window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.');
      if (!confirmed) return;
      
      setIsDeleting(true);
      try {
        await AdminService.deleteBlog(editBlog.id);
        setMessage({
          type: 'success',
          content: 'Blog post deleted successfully!'
        });
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } catch (error: any) {
        console.log(error);
        setMessage({
          type: 'error',
          content: error.response?.data?.message || error.message || 'Error deleting blog post'
        });
        setTimeout(() => {
          setMessage(undefined);
        }, 3000);
      } finally {
        setIsDeleting(false);
      }
    }

    async function editExistingBlog(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        
        // Validate required fields
        if (!data.title || data.title.trim() === '') {
            setMessage({
                type: 'error',
                content: 'Title (English) is required'
            });
            setTimeout(() => setMessage(undefined), 2500);
            return;
        }

        setIsLoading(true);
        setUploadProgress(0);
        
        try {
            if (!editBlog?.id) return
            
            // Convert cover image to WebP before submitting if it's a File object
            let processedCover = data.cover;
            
            if (processedCover instanceof File) {
              try {
                setUploadProgress(30);
                processedCover = await convertToWebP(processedCover);
                setUploadProgress(70);
                setMessage({
                  type: 'success',
                  content: 'Cover image optimized for web'
                });
                setTimeout(() => {
                  setMessage(undefined);
                }, 2000);
              } catch (error) {
                console.error('Error converting cover to WebP:', error);
                setMessage({
                  type: 'error',
                  content: 'Uploading cover in original format'
                });
                // Keep original file if conversion fails
              }
            }
            
            const newData = {
                blog_id: editBlog.id, 
                title: data.title,
                title_es: data.title_es,
                title_pt: data.title_pt,
                description: data.description,
                description_es: data.description_es,
                description_pt: data.description_pt,
                overview: data.overview,
                overview_es: data.overview_es,
                overview_pt: data.overview_pt,
                cover: processedCover
            }
            
            setUploadProgress(90);
            const formData = jsonToFormData(newData);
            await AdminService.editBlog(formData);
            setUploadProgress(100);
            
            setMessage({
                type: 'success',
                content: 'Blog post updated successfully!'
            });
            
            // Delay reload to show success message
            setTimeout(() => {
                window.location.reload();
            }, 1500);
            
        } catch (error: any) {
            console.log(error);
            setMessage({
              type: 'error',
              content: error.response?.data?.message || error.message || 'Error updating blog post'
            });
            setTimeout(() => {
              setMessage(undefined);
            }, 3000);
        } finally {
            setIsLoading(false);
            setUploadProgress(0);
        }
    }

    return (
        <Modal onSubmit={editExistingBlog}>
            <div className="close" title="close"
            onClick={() => setEditBlog(undefined)}
            >
                <AiOutlineCloseCircle/>
            </div>
            
            <h1>Edit Blog Post</h1>
            
            {/* Progress Bar */}
            {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="upload-progress" style={{
                    margin: '10px 0',
                    height: '4px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '2px',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        height: '100%',
                        backgroundColor: '#4CAF50',
                        width: `${uploadProgress}%`,
                        transition: 'width 0.3s ease'
                    }}></div>
                </div>
            )}
            
            <div className="input-container">
                <GeneralInput
                label="Title (en) *"
                name="title"
                value={data.title || ''}
                onChange={onChange}
                placeholder="English title"
                required
                />
                <GeneralInput
                label="Title (es)"
                name="title_es"
                value={data.title_es || ''}
                onChange={onChange}
                placeholder="Spanish title"
                />
                <GeneralInput
                label="Title (pt)"
                name="title_pt"
                value={data.title_pt || ''}
                onChange={onChange}
                placeholder="Portuguese title"
                />
            </div>
            
            <div className="input-container">
                <GeneralInput
                label="Overview (en)"
                name="overview"
                value={data.overview || ''}
                onChange={onChange}
                placeholder="English overview"
                />
                <GeneralInput
                label="Overview (es)"
                name="overview_es"
                value={data.overview_es || ''}
                onChange={onChange}
                placeholder="Spanish overview"
                />
                <GeneralInput
                label="Overview (pt)"
                name="overview_pt"
                value={data.overview_pt || ''}
                onChange={onChange}
                placeholder="Portuguese overview"
                />
            </div>
            
            <div className="input-container">
                <GeneralInput
                label="Description (en)"
                name="description"
                value={data.description || ''}
                onChange={onChange}
                placeholder="English description"
                />
                <GeneralInput
                label="Description (es)"
                name="description_es"
                value={data.description_es || ''}
                onChange={onChange}
                placeholder="Spanish description"
                />
                <GeneralInput
                label="Description (pt)"
                name="description_pt"
                value={data.description_pt || ''}
                onChange={onChange}
                placeholder="Portuguese description"
                />
            </div>
            
            <div className="input-container">
                <FileInput
                    value={data.cover}
                    style={{height: '100px'}}
                    label="Cover Image (will be converted to WebP)"
                    accept=".png, .jpg, .jpeg, .heic, .webp"
                    onChange={handleFileChange}
                    deleteFile={() => {
                        setData(prevData => {
                            const { cover, ...rest } = prevData;
                            return rest as Blog;
                        });
                        setMessage({
                            type: 'error',
                            content: 'Cover image removed'
                        });
                        setTimeout(() => setMessage(undefined), 2000);
                    }}
                    />
                <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    marginTop: '4px',
                    fontStyle: 'italic'
                }}>
                    Supported formats: PNG, JPG, JPEG, HEIC, WebP (max 5MB)
                </div>
            </div>
            
            <div className="actions">
                <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Updating...' : 'Update Blog Post'}
                </button>
                <button 
                  type="button" 
                  onClick={deleteBlog}
                  disabled={isDeleting}
                  style={{
                    backgroundColor: 'red', 
                    border: 'none', 
                    color: 'white',
                    opacity: isDeleting ? 0.6 : 1,
                    cursor: isDeleting ? 'not-allowed' : 'pointer'
                  }}
                >
                    {isDeleting ? 'Deleting...' : 'Delete Blog Post'}
                </button>
            </div>
        </Modal>
    )
}